import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes, FaInstagram, FaFacebook } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';

const Nav = styled.nav`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.highlight} 0%,
    ${({ theme }) => theme.colors.primary} 25%,
    ${({ theme }) => theme.colors.secondary} 45%,
    ${({ theme }) => theme.colors.highlight} 100%
  );
  height: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem calc((100vw - 1200px) / 2);
  z-index: 10;
  position: sticky;
  top: 0;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const Logo = styled(Link)`
  color: ${({ theme }) => theme.colors.light};
  font-size: 2rem;
  font-weight: bold;
  text-decoration: none;
  padding: 0 1rem;
`;

const NavMenu = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  
  @media screen and (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
    flex-direction: column;
    width: 100%;
    position: absolute;
    top: 80px;
    left: 0;
    background: ${({ theme }) => theme.colors.secondary};
    transition: 0.3s ease-in-out;
  }
`;

const NavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.light};
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 0 1rem;
  height: 100%;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    color: ${({ theme }) => theme.colors.highlight};
  }`;

const AuthLink = styled(Link)`
  color: ${({ theme }) => theme.colors.light};
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 0.5rem 1rem;
  margin-left: 1rem;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.highlight};
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background: ${({ theme }) => theme.colors.highlight};
    color: white;
  }
  
  @media screen and (max-width: 768px) {
    margin: 0.5rem 1rem;
    width: calc(100% - 2rem);
    justify-content: center;
  }
  
  @media screen and (max-width: 768px) {
    width: 100%;
    padding: 1.5rem;
    text-align: center;
    
    &:hover {
      background: ${({ theme }) => theme.colors.accent};
    }
  }
`;

const MobileIcon = styled.div`
  display: none;
  
  @media screen and (max-width: 768px) {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(-100%, 60%);
    font-size: 1.8rem;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.light};
  }
`;

const SocialIcons = styled.div`
  display: flex;
  align-items: center;
  margin-left: 2rem;
  
  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const SocialIconLink = styled.a`
  color: ${({ theme }) => theme.colors.light};
  font-size: 1.5rem;
  margin-left: 1rem;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    color: ${({ theme }) => theme.colors.highlight};
    transform: translateY(-2px);
  }
`;

const LogoutButton = styled.button`
  color: ${({ theme }) => theme.colors.light};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.highlight};
  padding: 0.5rem 1rem;
  margin-left: 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: ${({ theme }) => theme.colors.highlight};
    color: white;
  }
`;

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsOpen(false);
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <Nav>
      <Logo to="/">Sonna</Logo>
      <MobileIcon onClick={toggleMenu}>
        {isOpen ? <FaTimes /> : <FaBars />}
      </MobileIcon>
      <NavMenu isOpen={isOpen}>
        <NavLink to="/" onClick={toggleMenu}>
          {isAuthenticated() && user?.role === 'artist' ? 'Dashboard' : 'Home'}
        </NavLink>
        <NavLink to="/booking" onClick={toggleMenu}>Book Now</NavLink>
        {isAuthenticated() && user?.role === 'artist' && (
          <NavLink to="/appointments" onClick={toggleMenu}>Appointments</NavLink>
        )}
        <NavLink to="/about" onClick={toggleMenu}>About</NavLink>
        <NavLink to="/contact" onClick={toggleMenu}>Contact</NavLink>
        
        <div style={{ 
          display: 'flex', 
          flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
          alignItems: 'center',
          marginLeft: window.innerWidth > 768 ? 'auto' : '0',
          padding: window.innerWidth <= 768 ? '1rem' : '0'
        }}>
          {!isAuthenticated() ? (
            <>
              <AuthLink to="/login" onClick={toggleMenu}>Log In</AuthLink>
              <AuthLink
                to="/register"
                onClick={toggleMenu}
                style={{
                  backgroundColor: 'transparent',
                  marginLeft: window.innerWidth > 768 ? '0.5rem' : '0',
                  marginTop: window.innerWidth <= 768 ? '0.5rem' : '0',
                }}
              >
                Sign Up
              </AuthLink>
            </>
          ) : (
            <LogoutButton type="button" onClick={handleLogout}>
              Log Out{user?.email ? ` (${user.email})` : ''}
            </LogoutButton>
          )}
          
          {window.innerWidth > 768 && (
            <SocialIcons>
              <SocialIconLink href="https://instagram.com" target="_blank" aria-label="Instagram">
                <FaInstagram />
              </SocialIconLink>
              <SocialIconLink href="https://facebook.com" target="_blank" aria-label="Facebook">
                <FaFacebook />
              </SocialIconLink>
            </SocialIcons>
          )}
        </div>
        
        {window.innerWidth <= 768 && (
          <SocialIcons style={{ 
            width: '100%', 
            justifyContent: 'center',
            padding: '1rem 0',
            borderTop: '1px solid rgba(255,255,255,0.1)'
          }}>
            <SocialIconLink href="https://instagram.com" target="_blank" aria-label="Instagram">
              <FaInstagram />
            </SocialIconLink>
            <SocialIconLink href="https://facebook.com" target="_blank" aria-label="Facebook">
              <FaFacebook />
            </SocialIconLink>
          </SocialIcons>
        )}
      </NavMenu>
    </Nav>
  );
};

export default Navbar;
